import _ from "lodash";
import { useContext, useEffect, useRef, useState } from "react";
import {
  closestCenter,
  DndContext,
  DragOverlay,
  DragStartEvent,
  DragEndEvent,
  PointerSensor,
  useDndMonitor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToWindowEdges } from "@dnd-kit/modifiers";
import { rectSortingStrategy, SortableContext } from "@dnd-kit/sortable";
import { nanoid } from "nanoid";
import { createPortal } from "react-dom";

import InfiniteScroll from "src/components/infiniteScroll";
import StageAction, { StageActionLoadingSkeleton } from "./stageAction";

import { DealRoomAction } from "src/types/dealRoom";

import { DealRoomActionPlanContext } from "../context";
import { NEW_ACTION_INITIAL_STATE } from "src/utils/dealRoom.helper";

import styles from "./styles.module.scss";

interface ReorderStagesProps {
  actions: DealRoomAction[];
  onDragStart: (e: DragStartEvent) => void;
  onDragEnd: (updatedActionsInOrder: DealRoomAction[], stageId: string) => void;
  stageId: string;
}

const ReorderMonitor: React.FC<ReorderStagesProps> = ({
  actions,
  onDragStart,
  onDragEnd,
  stageId,
}) => {
  useDndMonitor({
    onDragStart(e: DragStartEvent) {
      onDragStart(e);
    },

    onDragEnd(event: DragEndEvent) {
      const { active, over } = event;

      if (!over || active.id === over.id) {
        return;
      }

      const oldIndex = actions.findIndex((action) => action.id === active.id);
      const newIndex = actions.findIndex((action) => action.id === over.id);

      const reorderedActions = [...actions];
      const [movedItem] = reorderedActions.splice(oldIndex, 1);
      reorderedActions.splice(newIndex, 0, movedItem);

      if (_.isEqual(actions, reorderedActions)) {
        return;
      }

      onDragEnd(reorderedActions, stageId);
    },
  });

  return null;
};

interface ActionsProps {
  stageId: string;
}

const StageActions = ({ stageId }: ActionsProps) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const idRef = useRef(nanoid(6));

  const { state, handleFetchActions, handleReorderAction } = useContext(
    DealRoomActionPlanContext,
  );

  const editMode = _.get(state, "editMode");
  const editingItem = _.get(state, "editingItem", {});

  // actions
  const actions = _.get(state, `actions[${stageId}]`, {});
  const items = _.get(actions, "items", []);
  const loading = _.get(actions, "loading", false);
  const loaded = _.get(actions, "loaded", false);
  const error = _.get(actions, "error", null);
  const newAction = _.get(actions, "newAction", {
    item: NEW_ACTION_INITIAL_STATE,
    loading: false,
    error: null,
  });
  const newActionItem = _.get(newAction, "item", {});
  const editAction = _.get(actions, "editAction", {
    item: null,
    loading: false,
    error: null,
  });
  const editActionItem = _.get(editAction, "item", {});

  const pagination = _.get(actions, "pagination", {
    currentPage: -1,
    totalPages: 0,
    recordsPerPage: 20,
    totalRecordsCount: 0,
    hasMoreItems: false,
  });
  const { currentPage, totalPages, hasMoreItems } = pagination;

  const [draggingAction, setDraggingAction] = useState<DealRoomAction | null>(
    null,
  );

  // initial loading for actions
  useEffect(() => {
    if (currentPage < 0) {
      handleFetchActions(stageId, 0);
    }
  }, []);

  const handleLoadMoreActions = () => {
    const nextPage = currentPage + 1;
    if (nextPage < totalPages) {
      handleFetchActions(stageId, nextPage);
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    if (active.id) {
      const actionToDrag = items.find((action) => action.id === active.id);
      if (actionToDrag) {
        setDraggingAction(actionToDrag);
      }
    }
  };

  return (
    <div className={styles.actions}>
      <InfiniteScroll
        hasMore={hasMoreItems}
        loading={loading}
        loadMore={handleLoadMoreActions}
        loader={StageActionLoadingSkeleton}
        className={styles.infiniteLoaderContainer}
      >
        <>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToWindowEdges]}
          >
            <ReorderMonitor
              actions={items}
              onDragStart={handleDragStart}
              onDragEnd={handleReorderAction}
              stageId={stageId}
            />
            <SortableContext
              id={idRef.current}
              items={_.map(
                items,
                (action: DealRoomAction) => _.get(action, "id") || "",
              )}
              strategy={rectSortingStrategy}
            >
              {items?.map((action: DealRoomAction) => {
                return (
                  <StageAction
                    action={
                      _.get(editActionItem, "id") === _.get(action, "id")
                        ? editActionItem
                        : action
                    }
                    editing={editMode}
                    isNewAction={false}
                    dragging={false}
                    key={_.get(action, "id")}
                    saving={editAction.loading}
                  />
                );
              })}
            </SortableContext>

            {createPortal(
              <DragOverlay modifiers={[restrictToWindowEdges]}>
                {draggingAction && (
                  <StageAction
                    action={draggingAction}
                    editing={false}
                    isNewAction={false}
                    key={"dragging-action"}
                    dragging={true}
                    saving={false}
                  />
                )}
              </DragOverlay>,
              document.body,
            )}
          </DndContext>
          {editMode && !loading && (
            <StageAction
              action={newActionItem}
              editing={editMode}
              isNewAction={true}
              dragging={false}
              key={"new-action"}
              saving={newAction.loading}
            />
          )}
        </>
      </InfiniteScroll>
    </div>
  );
};

export default StageActions;
